/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { ViewBase } from '../../views/view-base.js';
import { repeat } from 'lit/directives/repeat.js';
import { q1ExternalResourceListStyles } from './q1-external-resource-list-styles.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/*=== Q1 MODULES ===*/
import { q1DocFunctions, q1ExternalResourceFunctions } from '../../modules/q1-core-functions.js';
import { 
  externalResourceIconOptions, 
  externalResourceIconMap 
} from '../../models/external-resource-client-enums.js';

/*=== COMPONENT DEFINITION ===*/

export class Q1ExternalResourceList extends FormHostMixin(ViewBase) {
  static styles = [q1ExternalResourceListStyles];

  static properties = {
    subjectRef: { type: String },
    externalResources: { type: Array },
    externalResourcesLoading: { type: Boolean },
    iconName: { type: String },
    externalResourceId: { type: String },
  };

  constructor() {
    super();
    this.subjectRef = null;
    this.externalResources = [];
    this.externalResourcesLoading = false;
    this.iconName = '';
    this.externalResourceId = null;
  }

  /*=== ACTIONS ===*/

  async refreshExternalResources() {
    const { browseExternalResources } = q1ExternalResourceFunctions;
    this.externalResourcesLoading = true;

    try {
      const response = await browseExternalResources({
        subjectId: this.subjectRef.split('/')[1],
        subjectCollection: this.subjectRef.split('/')[0],
      });
      this.externalResources = response.data?.externalResources || [];
    } catch (error) {
      console.error('Failed to refresh external resources:', error);
    } finally {
      this.externalResourcesLoading = false;
    }
  }

  async createExternalResource() {
    const form = this.renderRoot.getElementById('add-resource-form');
    if (!form.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }

    const { createDoc } = q1DocFunctions;
    const formData = this._serializeForm(form);
    formData.externalData = { url: formData.externalUrl };
    delete formData.externalUrl;

    createDoc({ 
      docCollection: 'externalResources', 
      docData:{
        name: formData.name,
        title: formData.title,
        subject: this.subjectRef,
        icon: this.iconName,
        description: formData.description || '',
        externalData: formData.externalData,
      },
      }).then((response) => {;
        this.showToast(null, 'Exteral Resource created successfully!', 'success');
        this.refreshExternalResources();
      }).catch((error) => {
        console.error('Error calling function:', error);
        this.showToast(
          error.message, 
          'There was an error creating your external resource!', 'danger'
        );
      });
    this.hideDialog('createExternalResource');
  }

  async updateExternalResource() {
    const form = this.renderRoot.getElementById('edit-resource-form');
    
    if (!form.reportValidity()) {
      this.showToast('Please fix the form validation errors to continue', null, 'danger');
      return;
    }
    const {updateDoc} = q1DocFunctions;

    const formData = this._serializeForm(form);
    formData.externalData = { url: formData.externalUrl };
    delete formData.externalUrl;
    formData.icon = this.iconName;

    updateDoc({
      docCollection: 'externalResources',
      docId: this.externalResourceId,
      docUpdate: formData,
    }).then((response)=> {
      this.refreshExternalResources();
      this.showToast(null, 'Exteral Resource updated successfully!', 'success');
    }).catch((error) => {
      console.error('Error calling function:', error);
      this.showToast(error.message, 'Failed to update external resource:', error);
    });
    this.hideDialog('updateExternalResource');
  };

  async deleteExternalResource(id) {
    const { updateDoc } = q1DocFunctions;
  
    if (!confirm('Are you sure you want to delete this resource?')) return;
  
    try {
      await updateDoc({
        docCollection: 'externalResources',
        docId: id,
        docUpdate: { status: 'deleted' },
      });
      this.showToast(null, 'External resource deleted', 'success');
      this.refreshExternalResources();
    } catch (error) {
      console.error('Failed to delete external resource:', error);
      this.showToast(error.message, 'Error deleting external resource', 'danger');
    }
  }; 

  async openEditDialog(id) {
    const { getDoc } = q1DocFunctions;

    try {
      const response = await getDoc({
        docCollection: 'externalResources',
        docId: id,
      });
  
      const doc = response.data?.docData;
      if (!doc) throw new Error('No data returned');
  
      this.externalResourceId = id;
      this.iconName = doc.icon;
  
      const form = this.renderRoot.getElementById('edit-resource-form');
      if (!form) return;

      form.querySelector('[name="name"]').value = doc.name || '';
      form.querySelector('[name="title"]').value = doc.title || '';
      form.querySelector('[name="externalUrl"]').value = doc.externalData?.url || '';
      form.querySelector('[name="status"]').value = doc.status;
      form.querySelector('[name="description"]').value = doc.description || '';
  
      this.renderRoot.getElementById('edit-resource-dialog')?.show();
    } catch (error) {
      console.error('Failed to load resource for editing:', error);
    }
  }  

  hideDialog(type) {
    const id = type === 'updateExternalResource' ? 'edit-resource-dialog' : 'add-resource-dialog';
    this.renderRoot.getElementById(id)?.hide();
  }

  resetForm(){
    const form = this.renderRoot.getElementById('add-resource-form');
    if (form) {
      form.reset();
      this.iconName = '';
      this.externalResourceId = null;
    }
  }

  /*=== HANDLERS ===*/

  handleAddSubmit(event) {
    event.preventDefault();
    this.createExternalResource();
  }
  
  handleEditSubmit(event) {
    event.preventDefault();
    this.updateExternalResource();
  }
  
  /*=== GETTERS ===*/

  get addDialogTemplate() {
    return html`
      <sl-dialog id="add-resource-dialog" label='Add External Resource'>
        ${this.renderAddForm()}
      </sl-dialog>
    `;
  }
  
  get editDialogTemplate() {
    return html`
      <sl-dialog id="edit-resource-dialog" label='Edit External Resource'>
        ${this.renderEditForm()}
      </sl-dialog>
    `;
  }

  /*=== TEMPLATES ===*/

  renderAddForm() {
    const icondata = externalResourceIconMap[this.iconName] || {};
    const iconName = icondata.icon || this.iconName;
    const iconLibrary = icondata.iconLibrary;
  
    return html`
      <form id="add-resource-form" class="external-resource-form" @submit=${this.handleAddSubmit}>
        <sl-input name="externalUrl" label="URL" type="url" required></sl-input>
  
        <div class="icon-selector">
          <label class="icon-label">Icon</label>
          <sl-dropdown class="icon-dropdown" placement="bottom-start">
            <sl-button slot="trigger" caret>
              <sl-icon library=${iconLibrary} name=${iconName}></sl-icon>
            </sl-button>
            <sl-menu>
              ${externalResourceIconOptions.map(icon => html`
                <sl-menu-item @click=${() => this.iconName = icon.value}>
                  <sl-icon 
                    library=${icon.iconLibrary} 
                    name=${icon.icon} 
                    class="menu-icon"
                  ></sl-icon>
                  ${icon.label}
                </sl-menu-item>
              `)}
            </sl-menu>
          </sl-dropdown>
        </div>
  
        <sl-input 
          name="title" 
          label="Full title of the resource" 
          required 
        ></sl-input>
  
        <sl-input 
          name="name" 
          label="Shortened name of the resource. (Ex: Slack channel name)" 
          required 
        ></sl-input>
  
        <sl-textarea 
          name="description" 
          label="Member friendly description of the resource"
        ></sl-textarea>
  
        <button-bar slot="footer">
          <sl-button type="submit" variant="primary">
            <sl-icon slot="prefix" name="add_link" library="material"></sl-icon>
            Add Resource
          </sl-button>
          <sl-button @click=${() => this.hideDialog('createExternalResource')}>Cancel</sl-button>
        </button-bar>
      </form>
    `;
  }  
  
  renderEditForm() {
    const icondata = externalResourceIconMap[this.iconName] || {};
    const iconName = icondata.icon || this.iconName;
    const iconLibrary = icondata.iconLibrary;
  
    return html`
      <form id="edit-resource-form" class="external-resource-form" @submit=${this.handleEditSubmit}>
        <sl-input name="externalUrl" label="URL" type="url" required></sl-input>
  
        <div class="icon-selector">
          <label class="icon-label">Icon</label>
          <sl-dropdown class="icon-dropdown" placement="bottom-start">
            <sl-button slot="trigger" caret>
              <sl-icon library=${iconLibrary} name=${iconName}></sl-icon>
            </sl-button>
            <sl-menu>
              ${externalResourceIconOptions.map(icon => html`
                <sl-menu-item @click=${() => this.iconName = icon.value}>
                  <sl-icon library=${icon.iconLibrary} name=${icon.icon} class="menu-icon"></sl-icon>
                  ${icon.label}
                </sl-menu-item>
              `)}
            </sl-menu>
          </sl-dropdown>
        </div>
  
        <sl-input name="name" label="Name" required></sl-input>
        <sl-input name="title" label="Title" required></sl-input>
  
        <sl-select name="status" label="Status" required>
          <sl-option value="active">Active</sl-option>
          <sl-option value="inactive">Inactive</sl-option>
        </sl-select>
  
        <sl-textarea name="description" label="Description"></sl-textarea>
  
        <button-bar slot="footer">
          <sl-button type="submit" variant="primary">
            <sl-icon slot="prefix" name="edit" library="material"></sl-icon>
            Update Resource
          </sl-button>
          <sl-button @click=${() => this.hideDialog('updateExternalResource')}>Cancel</sl-button>
        </button-bar>
      </form>
    `;
  }  

  /*=== RENDER ===*/

  render() {
    return html`
      <div id="base-container" part="base">
        <query-bar>
          <h3 class="title">External Resources</h3>
          <div class="spacer"></div>
          <sl-button
            @click=${this.refreshExternalResources}
            size="small"
            pill
            ?disabled=${this.externalResourcesLoading}
          >
            ${this.externalResourcesLoading ? html`
              <sl-spinner slot="prefix"></sl-spinner>
              Refreshing...
            ` : html`
              <sl-icon library="material" name="refresh" slot="prefix"></sl-icon>
              Refresh
            `}
          </sl-button>
        </query-bar>
  
        <table-wrapper>
          <table class="q1">
            <tbody>
              ${!this.externalResources?.length ? html`
                <tr class="empty-placeholder">
                  <td colspan="3">
                    <span class="td-inner">
                      <sl-icon library="material" name="block"></sl-icon>
                      <span>No external resources yet.</span>
                    </span>
                  </td>
                </tr>
              ` : repeat(this.externalResources, er => er.id, er => {
                const icondata = externalResourceIconMap[er.icon];
                return html`
                  <tr>
                    <td>
                      ${icondata ? html`
                        <sl-icon
                          name=${icondata.icon}
                          library=${icondata.iconLibrary}
                          class="icon-left"
                        ></sl-icon>
                      ` : null}
                      ${er.name}
                    </td>
                    <td>
                      <sl-tag size="small" variant="neutral">${er.status}</sl-tag>
                    </td>
                    <td class="actions-col">
                      <sl-tooltip content="Edit">
                        <sl-button
                          variant="text"
                          size="medium"
                          title="Edit"
                          @click=${() => this.openEditDialog(er.id)}
                        >
                          <sl-icon slot="prefix" name="edit" library="material"></sl-icon>
                        </sl-button>
                      </sl-tooltip>
  
                      <sl-tooltip content="Delete">
                        <sl-button
                          variant="text"
                          size="medium"
                          title="Delete"
                          @click=${() => this.deleteExternalResource(er.id)}
                        >
                          <sl-icon slot="prefix" name="delete" library="material"></sl-icon>
                        </sl-button>
                      </sl-tooltip>
                    </td>
                  </tr>
                `;
              })}
            </tbody>
          </table>
        </table-wrapper>
      </div>
  
      <button-bar class="right-align">
        <sl-button
          variant="primary"
          @click=${() => {
            this.resetForm();
            this.renderRoot.getElementById('add-resource-dialog').show();
          }}
        >
          <sl-icon slot="prefix" name="add_link" library="material"></sl-icon>
          Add Resource
        </sl-button>
      </button-bar>
  
      ${this.addDialogTemplate}
      ${this.editDialogTemplate}
    `;
  }  
}

customElements.define('q1-external-resource-list', Q1ExternalResourceList);
